// ============================================
// AUTH MODULE
// Real backend authentication with JWT.
// Token persists in localStorage (key: vh_token)
// On mount, validates token by calling /auth/me.
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiLogin, apiGetMe, apiGetMeSilent, apiSsoVerify } from "../services/api";
import { useTheme } from "./ThemeContext";

// ── ROLE DEFINITIONS ───────────────────────
export const ROLES = {
  SUPER_ADMIN:  "super_admin",
  EVENT_ADMIN:  "event_admin",
  SUPERVISOR:   "supervisor",
  VOLUNTEER:    "volunteer",
};

// ── PERMISSIONS PER ROLE ───────────────────
// Drives can("...") checks throughout the app.
export const PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: [
    "view_dashboard", "create_event", "edit_event", "delete_event",
    "import_volunteers", "view_volunteers",
    "assign_duties", "send_whatsapp",
    "import_attendance", "view_attendance",
    "view_reports", "export_reports",
    "manage_roles", "manage_users", "view_all_data",
  ],
  [ROLES.EVENT_ADMIN]: [
    "view_dashboard", "create_event", "edit_event",
    "import_volunteers", "view_volunteers",
    "assign_duties", "send_whatsapp",
    "import_attendance", "view_attendance",
    "view_reports", "export_reports",
  ],
  [ROLES.SUPERVISOR]: [
    "view_dashboard",
    "view_volunteers",
    "assign_duties",
    "import_attendance", "view_attendance",
    "view_reports",
  ],
  [ROLES.VOLUNTEER]: [
    "view_own_duties",
    "view_own_attendance",
  ],
};

export const ROLE_COLORS = {
  [ROLES.SUPER_ADMIN]: "#00D4FF",
  [ROLES.EVENT_ADMIN]: "#00E5A0",
  [ROLES.SUPERVISOR]:  "#FF8C42",
  [ROLES.VOLUNTEER]:   "#9B8FFF",
};

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.EVENT_ADMIN]: "Event Admin",
  [ROLES.SUPERVISOR]:  "Supervisor",
  [ROLES.VOLUNTEER]:   "Volunteer",
};

// ── CONTEXT ────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: bootstrap auth.
  //
  // Boot logic (bulletproof against iframe localStorage partitioning):
  //
  // The fundamental problem we're solving: when this app is loaded inside
  // the GHL iframe, browsers may partition localStorage (Safari ITP, Chrome
  // strict-cookie mode, Firefox total cookie protection). That means the
  // token saved on first load may not be visible on subsequent loads of
  // the same iframe — even though the URL is identical. So we cannot rely
  // on localStorage as the source of truth inside an iframe.
  //
  // The fix: when we are inside an iframe, ALWAYS ask the GHL parent for
  // user identity (SSO). localStorage is treated as an optimization only —
  // if it has a valid token, we use it to skip the round-trip, but failure
  // of localStorage never blocks login. SSO is the canonical source.
  //
  // Race strategy: kick off SSO and localStorage-validation in parallel.
  // Whichever returns a valid user first wins. This gives us:
  //   • Fast path: localStorage hit → ~100ms to logged in
  //   • Reliable path: SSO succeeds → ~500ms to logged in
  //   • Both fail → show login screen (cleanest fallback)
  useEffect(() => {
    let cancelled = false;
    let settled   = false;

    const isInsideIframe = (() => {
      try { return window.self !== window.top; } catch (e) { return true; }
    })();

    const finish = (userObj) => {
      if (cancelled || settled) return;
      settled = true;
      setUser(userObj || null);
      setLoading(false);
    };

    // ────── Path 1: validate the localStorage token if present ──────
    // Use the SILENT variant so a 401 from a stale token doesn't fire
    // the global "session expired" banner — SSO may still be racing
    // and a successful SSO will quietly replace the stale token.
    const tryExistingToken = async () => {
      const token = localStorage.getItem("vh_token");
      if (!token) return null;
      try {
        const r = await apiGetMeSilent();
        if (cancelled) return null;
        return r.data.user;
      } catch (err) {
        // 401 here = stale token. Clear silently and let SSO take over.
        localStorage.removeItem("vh_token");
        return null;
      }
    };

    // ────── Path 2: do SSO via GHL postMessage protocol ──────
    const trySsoLogin = () => new Promise((resolve) => {
      if (!isInsideIframe) return resolve(null);

      let ssoSettled = false;
      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(retryTimer);
        clearTimeout(failTimer);
      };

      const handleEncrypted = async (encrypted) => {
        if (ssoSettled) return;
        ssoSettled = true;
        cleanup();
        try {
          const res  = await apiSsoVerify(encrypted);
          const data = res.data;
          if (data.success && data.token) {
            try { localStorage.setItem("vh_token", data.token); } catch (e) {}
            resolve(data.user);
          } else {
            console.warn("[SSO] verify failed:", data.message);
            resolve(null);
          }
        } catch (err) {
          console.warn("[SSO] verify error:", err.response?.data?.message || err.message);
          resolve(null);
        }
      };

      const onMessage = (event) => {
        if (ssoSettled || !event.data) return;
        // GHL may post the payload in several shapes. Accept all known forms.
        let encrypted = null;
        if (typeof event.data === "string") {
          encrypted = event.data;
        } else if (event.data.payload) {
          encrypted = event.data.payload;
        } else if (event.data.encryptedData) {
          encrypted = event.data.encryptedData;
        } else if (event.data.data && typeof event.data.data === "string") {
          encrypted = event.data.data;
        }
        if (!encrypted) return; // ignore unrelated messages
        handleEncrypted(encrypted);
      };

      window.addEventListener("message", onMessage);

      // Ask GHL repeatedly. The first request often races GHL's listener
      // being ready, so retries are essential — especially on tab return.
      const askParent = () => {
        try { window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*"); } catch (e) {}
      };
      askParent();
      const retryTimer = setInterval(askParent, 300);

      // 6 seconds is generous but bounded — long enough for cold-start
      // GHL pages, short enough that users don't stare at a spinner.
      const failTimer = setTimeout(() => {
        if (ssoSettled) return;
        ssoSettled = true;
        cleanup();
        resolve(null);
      }, 6000);
    });

    // ────── Strategy: race in iframe, sequential outside ──────
    (async () => {
      if (isInsideIframe) {
        // Both paths fire in parallel. First success wins.
        // If localStorage works → instant login.
        // If localStorage was wiped (the bug we're fixing) → SSO carries us.
        const tokenPromise = tryExistingToken();
        const ssoPromise   = trySsoLogin();

        // Resolve as soon as ONE succeeds
        let resolved = false;
        const accept = (user) => {
          if (resolved || !user) return;
          resolved = true;
          finish(user);
        };

        tokenPromise.then(accept);
        ssoPromise.then(accept);

        // After BOTH have finished, if nothing succeeded → login screen
        const [tokenResult, ssoResult] = await Promise.all([tokenPromise, ssoPromise]);
        if (!resolved && !cancelled) {
          if (tokenResult)      finish(tokenResult);
          else if (ssoResult)   finish(ssoResult);
          else                  finish(null);
        }
      } else {
        // Standalone (not in iframe) — token only, then login screen.
        const tokenUser = await tryExistingToken();
        finish(tokenUser);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Listen for 401-induced auth expiry from the axios interceptor
  useEffect(() => {
    const handler = () => { setUser(null); setError("Your session has expired. Please log in again."); };
    window.addEventListener("vh:auth-expired", handler);
    return () => window.removeEventListener("vh:auth-expired", handler);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const r = await apiLogin(email, password);
      localStorage.setItem("vh_token", r.data.token);
      setUser(r.data.user);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("vh_token");
    setUser(null);
    setError(null);
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    return PERMISSIONS[user.role]?.includes(permission) || false;
  }, [user]);

  const can = hasPermission;

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission, can, error, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ============================================
// LOGIN PAGE
// ============================================
export function LoginPage() {
  const { login, error } = useAuth();
  const { C } = useTheme();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy]         = useState(false);
  const [view, setView]         = useState("login"); // login | forgot

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    await login(email.trim(), password);
    setBusy(false);
  };

  if (view === "forgot") return <ForgotPasswordPage onBack={() => setView("login")} />;

  const inp = { width:"100%", padding:"11px 14px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:14, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", background:C.bg }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:32, fontWeight:800, color:C.text, fontFamily:"Georgia, serif", marginBottom:6 }}>VolunteerHub</div>
          <div style={{ fontSize:13, color:C.muted }}>VolunteerHub — Sign In</div>
        </div>

        <form onSubmit={handleSubmit} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"28px 26px" }}>
          {error && (
            <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:14, background:C.redSoft, border:`1px solid ${C.red}44`, color:C.red, fontSize:13, fontWeight:600 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Email</div>
            <input type="email" autoFocus value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" style={inp} />
          </div>

          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Password</div>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" style={inp} />
          </div>

          <button type="submit" disabled={busy || !email || !password}
            style={{ width:"100%", padding:"12px", background:busy?C.border:C.accent, border:"none", borderRadius:9, color:busy?C.muted:"#000", fontSize:14, fontWeight:700, cursor:busy?"not-allowed":"pointer", marginBottom:12 }}>
            {busy ? "Signing in…" : "Sign In"}
          </button>

          <div style={{ textAlign:"center" }}>
            <button type="button" onClick={() => setView("forgot")}
              style={{ background:"none", border:"none", color:C.accent, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              Forgot password?
            </button>
          </div>
        </form>

        <div style={{ textAlign:"center", marginTop:18, fontSize:11, color:C.muted }}>
          Need an account? Ask an administrator to invite you.
        </div>
      </div>
    </div>
  );
}

// ============================================
// FORGOT PASSWORD
// ============================================
function ForgotPasswordPage({ onBack }) {
  const { C } = useTheme();
  const [email, setEmail] = useState("");
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { apiForgotPassword } = await import("../services/api");
      const r = await apiForgotPassword(email.trim());
      setMsg({ type:"success", text: r.data.message });
    } catch {
      setMsg({ type:"success", text:"If that email is registered, a reset link has been sent." });
    }
    setBusy(false);
  };

  const inp = { width:"100%", padding:"11px 14px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:14, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", background:C.bg }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:28, fontWeight:800, color:C.text, fontFamily:"Georgia, serif", marginBottom:6 }}>Reset password</div>
          <div style={{ fontSize:13, color:C.muted }}>We'll email you a reset link</div>
        </div>

        <form onSubmit={handleSubmit} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"28px 26px" }}>
          {msg && (
            <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:14, background:C.greenSoft, border:`1px solid ${C.green}44`, color:C.green, fontSize:13, fontWeight:600 }}>
              {msg.text}
            </div>
          )}

          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Email</div>
            <input type="email" autoFocus value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" style={inp} />
          </div>

          <button type="submit" disabled={busy || !email}
            style={{ width:"100%", padding:"12px", background:busy?C.border:C.accent, border:"none", borderRadius:9, color:busy?C.muted:"#000", fontSize:14, fontWeight:700, cursor:busy?"not-allowed":"pointer", marginBottom:12 }}>
            {busy ? "Sending…" : "Send reset link"}
          </button>

          <div style={{ textAlign:"center" }}>
            <button type="button" onClick={onBack}
              style={{ background:"none", border:"none", color:C.muted, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}